import { EDGE_FUNCTION_HEADERS } from '@/lib/edge-functions';
import { createFileRoute } from '@tanstack/react-router';
import { formatDateTimeShort } from '@/lib/dates';
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Loader2, RefreshCw, Smartphone, Bell, BellOff, CheckCircle2, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export const Route = createFileRoute('/admin/push-diagnostics')({
  component: PushDiagnosticsPage,
});

const ENDPOINT = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-push-diagnostics`;
const STALE_MS = 5 * 60 * 1000;

async function getToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return session.access_token;
}

async function fetchList(): Promise<UserListItem[]> {
  const token = await getToken();
  const res = await fetch(`${ENDPOINT}?action=list`, {
    headers: { ...EDGE_FUNCTION_HEADERS, Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? res.statusText);
  const data = await res.json();
  return data.users;
}

async function fetchUser(id: string): Promise<UserDetail> {
  const token = await getToken();
  const res = await fetch(`${ENDPOINT}?action=user&id=${id}`, {
    headers: { ...EDGE_FUNCTION_HEADERS, Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? res.statusText);
  const data = await res.json();
  return data.user;
}

interface UserListItem {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  email: string | null;
  subscription_count: number;
  subscription_services: string[];
  last_push_received_at: string | null;
}

interface PushSub {
  id: string;
  endpoint: string;
  endpoint_short: string;
  service: 'apple' | 'fcm' | 'other';
  created_at: string;
  last_seen_at: string | null;
}

interface NotifRow {
  id: string;
  type: string;
  title: string | null;
  priority: string;
  created_at: string;
  push_received_at: string | null;
  push_shown_at: string | null;
  push_clicked_at: string | null;
  entity_id: string | null;
}

interface UserDetail {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  email: string | null;
  subscriptions: PushSub[];
  notifications: NotifRow[];
}

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function shortDate(iso: string | null): string {
  if (!iso) return '—';
  return formatDateTimeShort(iso);
}

function ServiceBadge({ service }: { service: string }) {
  const label = service === 'apple' ? 'Apple' : service === 'fcm' ? 'FCM' : service;
  const cls = service === 'apple'
    ? 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
    : service === 'fcm'
    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
    : 'bg-muted text-muted-foreground';
  return <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded ${cls}`}>{label}</span>;
}

function Avatar({ url, name }: { url: string | null; name: string | null }) {
  const initials = (name ?? '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  if (url) {
    return (
      <img
        src={url}
        alt={name ?? ''}
        className="w-9 h-9 rounded-full object-cover bg-muted flex-shrink-0"
        referrerPolicy="no-referrer"
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
      />
    );
  }
  return (
    <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold flex-shrink-0">
      {initials}
    </div>
  );
}

function BeaconDot({ value }: { value: string | null }) {
  if (!value) return <XCircle className="w-4 h-4 text-muted-foreground/40" />;
  return <CheckCircle2 className="w-4 h-4 text-green-500" />;
}

function PushDiagnosticsPage() {
  const [search, setSearch] = useState('');
  const [subscribedOnly, setSubscribedOnly] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['push-diagnostics-list'],
    queryFn: fetchList,
    staleTime: STALE_MS,
    refetchOnWindowFocus: false,
  });

  const detailQuery = useQuery({
    queryKey: ['push-diagnostics-user', selectedId],
    queryFn: () => fetchUser(selectedId!),
    enabled: !!selectedId,
    staleTime: STALE_MS,
    refetchOnWindowFocus: false,
  });

  const filtered = useMemo(() => {
    const users = listQuery.data ?? [];
    const q = search.toLowerCase().trim();
    return users.filter((u) => {
      if (subscribedOnly && u.subscription_count === 0) return false;
      if (!q) return true;
      return (
        (u.full_name ?? '').toLowerCase().includes(q) ||
        (u.email ?? '').toLowerCase().includes(q)
      );
    });
  }, [listQuery.data, search, subscribedOnly]);

  const isBackgroundFetching = listQuery.isFetching && !listQuery.isLoading;
  const detail = detailQuery.data;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-border bg-card flex-shrink-0">
        <Bell className="w-5 h-5 text-muted-foreground" />
        <h1 className="text-base font-semibold flex-1">Push Diagnostics</h1>

        {/* Background fetch indicator */}
        {isBackgroundFetching && (
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Loader2 className="w-3 h-3 animate-spin" />
            Updating…
          </span>
        )}

        {/* Subscribed only toggle */}
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
          <input
            type="checkbox"
            checked={subscribedOnly}
            onChange={(e) => setSubscribedOnly(e.target.checked)}
            className="rounded"
          />
          Subscribed only
        </label>

        {/* Search */}
        <input
          type="text"
          placeholder="Search name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-52 px-3 py-1.5 text-sm rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground outline-none focus:border-primary transition-colors"
        />

        {/* Refresh */}
        <button
          onClick={() => {
            listQuery.refetch();
            if (selectedId) detailQuery.refetch();
          }}
          disabled={listQuery.isFetching}
          title="Refresh"
          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors disabled:opacity-40"
        >
          <RefreshCw className={`w-4 h-4 ${listQuery.isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* User list */}
        <aside className="w-80 flex-shrink-0 border-r border-border overflow-y-auto">
          {listQuery.isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3 p-6 rounded-xl border border-border bg-card shadow-sm">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Loading users…</p>
              </div>
            </div>
          ) : listQuery.error ? (
            <div className="p-4 text-sm text-destructive">
              {listQuery.error instanceof Error ? listQuery.error.message : 'Failed to load'}
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">No users match.</div>
          ) : (
            <ul className="divide-y divide-border">
              {filtered.map((u) => (
                <li key={u.id}>
                  <button
                    onClick={() => setSelectedId(u.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-secondary/50 transition-colors ${
                      selectedId === u.id ? 'bg-primary/5 border-l-2 border-primary' : ''
                    }`}
                  >
                    <Avatar url={u.avatar_url} name={u.full_name} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{u.full_name ?? <em className="text-muted-foreground">No name</em>}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email ?? '—'}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        {u.subscription_count === 0 ? (
                          <BellOff className="w-3 h-3 text-muted-foreground/50" />
                        ) : (
                          <>
                            <Smartphone className="w-3 h-3 text-muted-foreground" />
                            <span className="text-[10px] text-muted-foreground">{u.subscription_count}</span>
                            {u.subscription_services.map((s) => (
                              <ServiceBadge key={s} service={s} />
                            ))}
                          </>
                        )}
                        {u.last_push_received_at && (
                          <span className="text-[10px] text-muted-foreground ml-auto">{relativeTime(u.last_push_received_at)}</span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Detail */}
        <main className="flex-1 overflow-y-auto">
          {!selectedId ? (
            <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
              Select a user to view push details
            </div>
          ) : detailQuery.isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : detailQuery.error ? (
            <div className="p-6 text-sm text-destructive">
              {detailQuery.error instanceof Error ? detailQuery.error.message : 'Failed to load user'}
            </div>
          ) : detail ? (
            <div className="p-6 space-y-6 max-w-4xl">
              {/* User header */}
              <div className="flex items-center gap-4">
                <Avatar url={detail.avatar_url} name={detail.full_name} />
                <div>
                  <h2 className="text-lg font-semibold">{detail.full_name ?? <em className="text-muted-foreground">No name</em>}</h2>
                  <p className="text-sm text-muted-foreground">{detail.email ?? '—'}</p>
                  <p className="text-xs text-muted-foreground font-mono">{detail.id}</p>
                </div>
              </div>

              {/* Devices */}
              <section>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  Devices &amp; Subscriptions ({detail.subscriptions.length})
                </h3>
                {detail.subscriptions.length === 0 ? (
                  <div className="flex items-center gap-2 p-4 rounded-lg border border-dashed border-border text-sm text-muted-foreground">
                    <BellOff className="w-4 h-4" />
                    No push subscriptions registered
                  </div>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {detail.subscriptions.map((s) => (
                      <div key={s.id} className="p-4 rounded-lg border border-border bg-card space-y-2">
                        <div className="flex items-center justify-between">
                          <ServiceBadge service={s.service} />
                          <span className="text-[10px] text-muted-foreground">registered {relativeTime(s.created_at)}</span>
                        </div>
                        <p className="text-xs font-mono text-muted-foreground break-all">{s.endpoint_short}</p>
                        {s.last_seen_at && (
                          <p className="text-xs text-muted-foreground">Last seen: {shortDate(s.last_seen_at)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Notifications */}
              <section>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  Notifications (last {detail.notifications.length})
                </h3>
                {detail.notifications.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No notifications.</p>
                ) : (
                  <div className="border border-border rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50 border-b border-border">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium text-muted-foreground">Type</th>
                          <th className="text-left px-3 py-2 font-medium text-muted-foreground">Title</th>
                          <th className="text-left px-3 py-2 font-medium text-muted-foreground">Created</th>
                          <th className="px-3 py-2 font-medium text-muted-foreground text-center" title="push_received_at">Rcvd</th>
                          <th className="px-3 py-2 font-medium text-muted-foreground text-center" title="push_shown_at">Shown</th>
                          <th className="px-3 py-2 font-medium text-muted-foreground text-center" title="push_clicked_at">Clicked</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {detail.notifications.map((n) => (
                          <tr key={n.id} className="hover:bg-muted/30 transition-colors">
                            <td className="px-3 py-2">
                              <span className="inline-block bg-muted text-muted-foreground px-1.5 py-0.5 rounded text-[10px] font-mono">
                                {n.type.replace('community.', '')}
                              </span>
                            </td>
                            <td className="px-3 py-2 max-w-[200px] truncate text-foreground">
                              {n.title ?? <em className="text-muted-foreground">—</em>}
                            </td>
                            <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{shortDate(n.created_at)}</td>
                            <td className="px-3 py-2 text-center">
                              <div className="flex justify-center" title={n.push_received_at ? shortDate(n.push_received_at) : 'Not received'}>
                                <BeaconDot value={n.push_received_at} />
                              </div>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <div className="flex justify-center" title={n.push_shown_at ? shortDate(n.push_shown_at) : 'Not shown'}>
                                <BeaconDot value={n.push_shown_at} />
                              </div>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <div className="flex justify-center" title={n.push_clicked_at ? shortDate(n.push_clicked_at) : 'Not clicked'}>
                                <BeaconDot value={n.push_clicked_at} />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>
          ) : null}
        </main>
      </div>
    </div>
  );
}
